import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import {
  FormBase,
  FormModal,
  type IBaseModalProps,
} from '@gbs-monorepo-packages/common';
import {
  type RenamePageSchema,
  renamePageSchema,
} from '@gbs-monorepo/edge/formSchemas/pageSchema';
import { zodResolver } from '@hookform/resolvers/zod';

import { VALIDATION_LIMITS } from '../../../../constants/ValidationRules';

interface IRenamePageModalProps extends Partial<
  Omit<IBaseModalProps, 'children' | 'onOpenChange'>
> {
  onAccept: (newTitle: string) => void;
  onDecline: () => void;
  open: boolean;
  pageTitle: string;
  waitToOpen?: boolean;
}

export const RenamePageModal = ({
  onAccept,
  onDecline,
  open,
  pageTitle,
  waitToOpen = false,
  ...props
}: IRenamePageModalProps): JSX.Element | null => {
  const renamePage = useForm<RenamePageSchema>({
    resolver: zodResolver(renamePageSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      newTitle: pageTitle,
    },
  });

  const {
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
  } = renamePage;

  const values = watch();

  useEffect(() => {
    if (open) {
      reset({
        newTitle: pageTitle,
      });
    }
  }, [open, pageTitle, reset]);

  const onSubmit: SubmitHandler<RenamePageSchema> = useCallback(
    (data: RenamePageSchema) => {
      onAccept?.(data.newTitle);
      reset({
        newTitle: '',
      });
    },
    [onAccept, reset]
  );

  const handleDeclineRenamePage = useCallback(() => {
    onDecline?.();
    reset({
      newTitle: pageTitle,
    });
  }, [onDecline, pageTitle, reset]);

  return !open ? null : (
    <FormBase.Provider {...renamePage}>
      <FormModal
        acceptText="Change"
        dataCy="title-renamePage"
        declineText="Cancel"
        mainText={`What is the new title of the page ${pageTitle}?`}
        {...props}
        onAccept={handleSubmit(onSubmit)}
        onDecline={handleDeclineRenamePage}
        onOpenChange={handleDeclineRenamePage}
        open={!waitToOpen}
        disabled={!isValid}
      >
        <FormBase.Content>
          <FormBase.InputContent
            dataCy="label-pageName"
            filled={!!values.newTitle}
            label="Title"
            errorMessage={errors.newTitle?.message}
          >
            <FormBase.InputText
              dataCy="input-pageName"
              id="newTitle"
              name="newTitle"
              autoComplete="off"
              value={values.newTitle}
              required
              type="text"
              maxLength={VALIDATION_LIMITS.COURSE.TITLE_MAX}
            />
          </FormBase.InputContent>
        </FormBase.Content>
      </FormModal>
    </FormBase.Provider>
  );
};
